import { Callout } from "../components/Callout.js";
import { ScreenshotBox } from "../components/ScreenshotBox.js";

export const meta = {
  title: "Client ID 管理（複数送信者）",
  updatedAt: "2026-08-03",
};

export function Content() {
  return (
    <>
      <p>
        Sign Extension は複数の Client ID を 1 つのテナント内で並行利用できます。
        部署別・送信者別・本番 / sandbox の切り替えなどに対応します。
      </p>

      <h2>典型的なユースケース</h2>
      <ul>
        <li>営業部用と法務部用で別々のクラウドサイン契約を使い分けたい</li>
        <li>送信者の名前として「○○部 田中」「○○部 佐藤」と分けたい</li>
        <li>本番運用と並行して sandbox での検証も行いたい</li>
      </ul>

      <h2>登録</h2>
      <p>
        「<strong>接続設定</strong>」タブ →「クライアントID一覧」の
        「<strong>+ 新規追加</strong>」から登録します。
        ラベル名で識別するので、わかりやすい名前を付けてください。
      </p>
      <ScreenshotBox src="/guide/screenshots/clientid-mgmt-1.png" caption="Client ID 一覧" />

      <h2>「デフォルト」設定</h2>
      <p>
        各 Client ID の「<strong>デフォルトに設定</strong>」ボタンで、
        送信モーダルで初期選択される Client ID を指定できます。
        「デフォルト」バッジが付いているものが現在の初期選択です。
      </p>
      <ul>
        <li>デフォルトはテナント全体で 1 つだけです（本番 / sandbox の区別はありません）</li>
        <li>別の Client ID をデフォルトにすると、元のデフォルトは自動で解除されます</li>
        <li>変更はいつでも可能です</li>
      </ul>

      <h2>送信モーダルでの切り替え</h2>
      <p>
        送信モーダルの「ファイル選択」ステップに「<strong>送信元クライアント ID</strong>」
        ドロップダウンがあります。ここで Client ID を選ぶと、その Client ID の
        クラウドサインアカウントから書類が送信されます。クラウドサイン上の送信者名は、
        選択した Client ID の発行者になります。
      </p>
      <ScreenshotBox src="/guide/screenshots/clientid-mgmt-2.png" caption="送信モーダルの送信元 Client ID 切替" />

      <h2>ステータス同期と Client ID の紐付け</h2>
      <p>
        送信時に使った Client ID は送信ログに記録されます。
        ステータス同期も同じ Client ID を使って書類を取得します。
        これにより、複数の Client ID が並行運用されていても、
        各書類のステータスが正しく同期されます。
      </p>

      <Callout type="warning" title="Client ID を削除すると">
        削除した Client ID で過去に送信した書類のステータス同期は止まります
        （対象書類は「取得不可」として扱われます）。ステータスが当時のまま
        固定されるので、影響を理解した上で削除してください。
      </Callout>

      <h2>Client ID ごとに送信設定を変える</h2>
      <p>
        <strong>送信モード</strong>・<strong>書類の転送設定</strong>・
        <strong>受信者氏名の敬称</strong>の 3 つは、Client ID ごとに変えられます。
        「営業部は下書きモード、法務部は直接送信モード」といった運用が可能です。
      </p>

      <h3>デフォルト設定</h3>
      <p>
        「<strong>接続設定</strong>」タブの「クライアントID一覧」の先頭にある
        「<strong>デフォルト設定</strong>」の「編集」から設定します。
        ここで設定した内容が、個別設定を持たないすべての Client ID に適用されます。
        まずはここだけ設定すれば十分です。
      </p>

      <h3>Client ID ごとの上書き</h3>
      <p>
        各 Client ID の「<strong>送信設定</strong>」ボタンから、その Client ID だけの
        設定に切り替えられます。
      </p>
      <ul>
        <li>「デフォルト設定を使用」— 既定。デフォルトを変更すると自動で反映されます</li>
        <li>「このクライアントIDで個別に設定」— 項目ごとにデフォルトを上書きします</li>
      </ul>
      <p>
        個別設定を選んだ場合も、各項目に「<strong>デフォルト設定に従う</strong>」が
        あります。上書きしたい項目だけ変更すれば、それ以外はデフォルトに追従し続けます
        （例: 送信モードだけ変えて、敬称はデフォルトのまま）。
      </p>
      <p>
        一覧では各 Client ID に「送信設定：デフォルト」または「送信設定：個別」の
        バッジが付くので、どこに個別設定があるか一目で確認できます。
      </p>

      <Callout type="tip" title="送信モーダルでの見え方">
        送信モーダルで送信元 Client ID を切り替えると、確認画面の送信モードや
        転送許可の既定値も切り替え先の設定に追従します。
        すでに宛先に入力済みの氏名は書き換わりません（以降に追加した宛先にだけ
        新しい敬称が付きます）。
      </Callout>

      <Callout type="info" title="HubSpot への書き戻しは共通">
        ステータスや書類 URL の書き戻し先プロパティ、締結完了ファイルの格納方式は
        Client ID ごとには分けられません（「プロパティ設定」タブでオブジェクト単位に
        設定します）。Client ID で分けられるのは<strong>送信するときの挙動</strong>だけ、と
        覚えてください。
      </Callout>

      <h2>ユーザーごとに使える Client ID を絞る</h2>
      <p>
        「<strong>HubSpot ユーザー権限</strong>」タブで、HubSpot ユーザーごとに
        使用できる Client ID を指定できます。指定すると、そのユーザーが HubSpot の
        レコードから送信モーダルを開いたとき、許可された Client ID だけが
        ドロップダウンに表示されます。
      </p>
      <ul>
        <li>何も設定していないユーザーは、<strong>すべての Client ID を使えます</strong>（既定の動作）</li>
        <li>1 つだけ許可した場合、そのユーザーには選択肢が出ず、自動でその Client ID が使われます</li>
        <li>ユーザーの識別は HubSpot のユーザー ID で行うため、Sign Extension のダッシュボードに
          ログインしていない HubSpot ユーザーにも適用されます</li>
      </ul>
      <p style={{ fontSize: 13, color: "#6b6b68" }}>
        HubSpot ユーザー一覧の取得には HubSpot 側の権限が必要です。一覧が空の場合は
        「HubSpot 再認可」を実施してください。
      </p>

      <Callout type="info" title="この機能の位置づけ">
        Client ID の制限は、<strong>送信時の取り違えを防ぐための運用上の仕組み</strong>です。
        厳密なアクセス制御・セキュリティ機能ではありません。
        情報漏洩の防止など、確実な分離が必要な場合は、クラウドサインのアカウント自体を
        分ける、または HubSpot 側でレコードのアクセス権を制限する運用と
        組み合わせてご検討ください。
      </Callout>

      <h2>各 Client ID に対してできる操作</h2>
      <ul>
        <li><strong>接続テスト</strong>: Client ID が有効か即時確認</li>
        <li><strong>デフォルトに設定</strong>: 送信モーダルの初期選択にする</li>
        <li><strong>削除</strong>: いつでも削除可（上記注意点あり）</li>
      </ul>
      <p style={{ fontSize: 13, color: "#6b6b68" }}>
        ラベルや Client ID 自体の編集機能はありません。変更したい場合は削除して
        新規に登録し直してください。
      </p>

      <Callout type="tip" title="運用のお勧め">
        本番運用に入る前に、必ず sandbox の Client ID で 1 回送信テストして
        ください。本番 Client ID のテストは実際の書類が作成されてしまうため、
        sandbox で動作確認 → 本番送信、の流れが安全です。
      </Callout>
    </>
  );
}
